import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Flex, Text, Spinner, Link, SimpleGrid, HStack, Button, VStack } from '@chakra-ui/react';
import Markdown from './Markdown';
import Toc from './Toc';
import { useAuth } from '../auth/AuthContext';
import { useDocs } from '../docs/DocsContext';
import { headingId } from '../lib/slug';

function useMarkdown(slug, loadDoc) {
  const [state, setState] = useState({ loading: true, text: '', error: null });
  useEffect(() => {
    if (!slug) { setState({ loading: false, text: '', error: null }); return undefined; }
    let alive = true;
    setState({ loading: true, text: '', error: null });
    loadDoc(slug)
      .then((text) => alive && setState({ loading: false, text, error: null }))
      .catch((error) => alive && setState({ loading: false, text: '', error }));
    return () => { alive = false; };
  }, [slug, loadDoc]);
  return state;
}

function PrevNext({ pages, index }) {
  const prev = pages[index - 1];
  const next = pages[index + 1];
  const card = {
    display: 'block', bg: 'white', borderWidth: '1px', borderColor: 'neutral.200',
    borderRadius: '8px', boxShadow: 'sm', px: 4, py: 3.5, _hover: { borderColor: 'brand.300', textDecoration: 'none' },
  };
  return (
    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mt={7}>
      {prev ? (
        <Link as={RouterLink} to={`/${prev.slug}`} {...card}>
          <Text fontSize="11px" color="neutral.500" mb={1}>이전</Text>
          <Text fontSize="14px" fontWeight="600" color="neutral.800">{prev.title}</Text>
        </Link>
      ) : <Box />}
      {next && (
        <Link as={RouterLink} to={`/${next.slug}`} {...card} textAlign="right">
          <Text fontSize="11px" color="neutral.500" mb={1}>다음</Text>
          <Text fontSize="14px" fontWeight="600" color="neutral.800">{next.title}</Text>
        </Link>
      )}
    </SimpleGrid>
  );
}

// 로그인하지 않은 사람이 비공개 문서 주소로 들어왔을 때. 승인 → 로그인이라는
// 순서를 분명히 알려 주고, 지금 바로 읽을 수 있는 문서로 돌려보낸다.
function LoginRequired({ page, openDocs }) {
  const location = useLocation();
  const next = `${location.pathname}${location.search}`;
  return (
    <Box maxW="740px" py={6}>
      <Text fontSize="22px" fontWeight="700" letterSpacing="-0.03em" mb={2}>
        로그인이 필요한 문서입니다
      </Text>
      <Text fontSize="14.5px" color="neutral.600" mb={5}>
        「{page.title}」 문서는 회원에게 제공됩니다. <b>회원가입이 승인된 뒤 레이블리 계정으로
        로그인하시면</b> 서비스 이용 방법을 담은 전체 문서를 보실 수 있습니다.
      </Text>

      <HStack spacing={2.5} mb={7}>
        <Button as={RouterLink} to={`/login?next=${encodeURIComponent(next)}`}
          bg="brand.500" color="white" _hover={{ bg: 'brand.600', textDecoration: 'none' }}
          fontSize="14px" h="40px" px={5}>
          로그인
        </Button>
      </HStack>

      <Box borderTopWidth="1px" borderColor="neutral.200" pt={5}>
        <Text fontSize="12px" fontWeight="600" letterSpacing="0.08em" textTransform="uppercase"
          color="neutral.500" mb={3}>
          로그인 없이 볼 수 있는 문서
        </Text>
        <VStack align="stretch" spacing={1.5}>
          {openDocs.map((d) => (
            <Link key={d.slug} as={RouterLink} to={`/${d.slug}`} fontSize="14px" color="brand.700"
              textDecoration="underline" textUnderlineOffset="2px" w="fit-content">
              {d.title}
            </Link>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}

export default function DocPage({ page }) {
  const { signedIn, roleError } = useAuth();
  const { visiblePages: pages, loadDoc } = useDocs();
  // 열람 가능 여부는 원장이 준다 — 피드 모드는 서버 판정, 정적 모드는 canView. 본문 요청은 그 뒤에만 한다.
  const allowed = Boolean(page.visible);
  const index = pages.findIndex((p) => p.slug === page.slug);
  const { loading, text, error } = useMarkdown(allowed ? page.slug : null, loadDoc);

  const headings = useMemo(
    () =>
      text
        .split('\n')
        .filter((line) => line.startsWith('## ') && !line.startsWith('### '))
        .map((line) => {
          const title = line.replace(/^##\s+/, '').trim();
          return { title, id: headingId(title) };
        }),
    [text]
  );

  useEffect(() => {
    document.title = `${page.title} · 레이블리 도움말`;
    window.scrollTo(0, 0);
  }, [page]);

  if (!allowed) {
    if (!signedIn) return <LoginRequired page={page} openDocs={pages} />;
    return (
      <Box maxW="740px" py={6}>
        <Text fontSize="22px" fontWeight="700" letterSpacing="-0.03em" mb={2}>열람 권한이 없는 문서입니다</Text>
        <Text fontSize="14.5px" color="neutral.600">
          {roleError
            ? '지금 회원 정보를 확인하지 못해 공개 문서만 보여 드리고 있습니다. 새로고침해도 같다면 레이블리에 알려 주세요.'
            : '이 문서는 다른 역할의 회원에게 제공됩니다. 잘못 안내받으셨다면 레이블리에 문의해 주세요.'}
        </Text>
      </Box>
    );
  }

  return (
    <Flex gap={8} align="flex-start">
      <Box flex="1" minW={0} maxW="740px" pb={24}>
        <HStack spacing={2} fontSize="12px" color="neutral.500" mb={3}>
          <Text>도움말</Text><Text color="neutral.400">/</Text>
          <Text>{page.group}</Text><Text color="neutral.400">/</Text>
          <Text color="neutral.600">{page.title}</Text>
        </HStack>

        {loading && <Spinner size="sm" color="brand.500" mt={6} />}
        {error && <Text color="status.error">문서를 불러오지 못했습니다. 새로고침해 주세요.</Text>}
        {!loading && !error && <Markdown>{text}</Markdown>}

        <PrevNext pages={pages} index={index} />
      </Box>

      <Toc headings={headings} />
    </Flex>
  );
}
